import type { BaseUser } from "@models";
import type { Fragment } from "@tiptap/pm/model";
import {
  normalizeTiptapUserId,
  resolveTiptapUserAuthor,
} from "../version-authoring";
import { shouldPreserveVersionComparisonTableNodeName } from "./table-version-comparison";

export type SimpleEditorVersionComparisonAuthor = {
  id: string;
  name: string;
  color: string;
};

export type SimpleEditorVersionComparisonState = {
  fromVersion: number;
  fromTitle: string;
  authors: SimpleEditorVersionComparisonAuthor[];
};

const VERSION_COMPARISON_COLORS = [
  "#ff9c9c",
  "#a7dcf8",
  "#ffc99f",
  "#c8b6ff",
  "#9ee6d3",
  "#f7c5da",
];

export const UNKNOWN_VERSION_COMPARISON_USER_ID = "unknown-user";
export const DOVETAIL_VERSION_COMPARISON_USER_ID = "dovetail";
export const DOVETAIL_VERSION_COMPARISON_COLOR = "#f4c98c";

export const getVersionComparisonAuthorId = (userId?: string | null) => {
  const normalizedUserId = userId ? normalizeTiptapUserId(userId) : "";

  return normalizedUserId || UNKNOWN_VERSION_COMPARISON_USER_ID;
};

export const getVersionComparisonColorForUserId = (userId: string) => {
  if (userId === DOVETAIL_VERSION_COMPARISON_USER_ID) {
    return DOVETAIL_VERSION_COMPARISON_COLOR;
  }

  const hash = Array.from(userId || UNKNOWN_VERSION_COMPARISON_USER_ID).reduce(
    (result, character) => result + character.charCodeAt(0),
    0
  );

  return VERSION_COMPARISON_COLORS[hash % VERSION_COMPARISON_COLORS.length];
};

const getUniqueVersionComparisonColorForUserId = (
  userId: string,
  usedColors: Set<string>
) => {
  const preferredColor = getVersionComparisonColorForUserId(userId);

  if (userId === DOVETAIL_VERSION_COMPARISON_USER_ID) {
    usedColors.add(preferredColor);
    return preferredColor;
  }

  const preferredIndex = VERSION_COMPARISON_COLORS.indexOf(preferredColor);

  for (let offset = 0; offset < VERSION_COMPARISON_COLORS.length; offset += 1) {
    const color =
      VERSION_COMPARISON_COLORS[
        (Math.max(preferredIndex, 0) + offset) %
          VERSION_COMPARISON_COLORS.length
      ];

    if (!usedColors.has(color)) {
      usedColors.add(color);
      return color;
    }
  }

  return preferredColor;
};

export const getVersionComparisonRemovedColor = (color: string) => {
  const match = color.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);

  if (!match) {
    return color;
  }

  const [, red, green, blue] = match;
  return `rgba(${Number.parseInt(red, 16)}, ${Number.parseInt(
    green,
    16
  )}, ${Number.parseInt(blue, 16)}, 0.45)`;
};

export const getVersionComparisonDiffBackgroundColor = ({
  color,
  diffType,
}: {
  color: string;
  diffType?: string | null;
}) => {
  if (diffType === "block-update") {
    return "transparent";
  }

  if (diffType === "inline-delete" || diffType === "block-delete") {
    return getVersionComparisonRemovedColor(color);
  }

  return color;
};

export const shouldShowVersionComparisonDiffInLegend = ({
  decorationDiffType,
  type,
}: {
  decorationDiffType?: string | null;
  type?: string | null;
}) => (decorationDiffType ?? type) !== "block-update";

export const getVersionComparisonDiffDecorationStyle = (
  backgroundColor: string
) =>
  `--version-comparison-diff-background-color: ${backgroundColor}; background-color: ${backgroundColor}`;

export const getVersionComparisonDiffDecorationAttributes = ({
  backgroundColor,
  content,
  decorationDiffType,
  diffContentType,
  diffType,
  nodeTypeName,
  userId,
  userName,
}: {
  backgroundColor: string;
  content?: Fragment | null;
  decorationDiffType?: string | null;
  diffContentType?: string | null;
  diffType?: string | null;
  nodeTypeName?: string | null;
  userId: string;
  userName: string;
}): Record<string, string | undefined> => {
  const attributes: Record<string, string | undefined> = {
    "data-diff-content-type": diffContentType ?? "",
    "data-diff-type": decorationDiffType ?? diffType ?? "",
    "data-diff-user-id": userId,
    "data-diff-user-name": userName,
    style: getVersionComparisonDiffDecorationStyle(backgroundColor),
  };

  if (
    shouldPreserveVersionComparisonTableNodeName({
      content,
      diffType,
      nodeTypeName,
    })
  ) {
    attributes.nodeName = undefined;
  }

  return attributes;
};

type VersionComparisonUser = Pick<
  BaseUser,
  "email" | "firstName" | "id" | "lastName"
>;

export const getVersionComparisonAuthorsForIds = ({
  authorIds,
  orgUsers,
  trackedChangesUserId,
  trackedChangesUserName,
}: {
  authorIds: Array<string | null | undefined>;
  orgUsers: VersionComparisonUser[];
  trackedChangesUserId: string;
  trackedChangesUserName: string;
}) => {
  const orgUsersById = new Map(orgUsers.map((user) => [user.id, user]));
  const usedColors = new Set<string>();
  const seenAuthorIds = new Set<string>();

  return authorIds.reduce<SimpleEditorVersionComparisonAuthor[]>(
    (authors, authorId) => {
      const id = getVersionComparisonAuthorId(authorId);

      if (seenAuthorIds.has(id)) {
        return authors;
      }

      seenAuthorIds.add(id);
      const resolvedAuthor =
        id === DOVETAIL_VERSION_COMPARISON_USER_ID
          ? { name: "Dovetail" }
          : id === UNKNOWN_VERSION_COMPARISON_USER_ID
            ? { name: "Unknown user" }
            : resolveTiptapUserAuthor({
                trackedChangesUserId,
                trackedChangesUserName,
                userId: id,
                usersById: orgUsersById,
              });

      authors.push({
        id,
        name: resolvedAuthor.name,
        color: getUniqueVersionComparisonColorForUserId(id, usedColors),
      });

      return authors;
    },
    []
  );
};
